import { Wand2, RotateCcw, Beaker, Gauge } from "lucide-react";
import { TRANSLATIONS } from "@/i18n";
import { TOP_MODES, TOP_MODE_COLORS } from "@/constants/diplomate";

const ICONS = {
  cordialement: Wand2,
  reverse: RotateCcw,
  antidote: Beaker,
  bullshit: Gauge,
};

/**
 * TopModeSwitcher — 4 segmented pills.
 * Desktop: 4 pills en grille équilibrée.
 * Mobile:  scroll horizontal avec snap; le mode actif reste toujours visible.
 * Ordre IMPÉRATIF : cordialement, reverse, antidote, bullshit.
 */
export const TopModeSwitcher = ({ lang, topMode, onChange }) => {
  const t = TRANSLATIONS[lang];
  return (
    <div className="mb-8 sm:mb-10" data-testid="top-mode-switcher">
      <div className="flex items-center gap-3 mb-3">
        <span className="dossier-tag font-bold">{t.sectionTopModeLabel}</span>
        <span className="flex-1 h-px bg-black/15" />
      </div>

      <div
        role="tablist"
        aria-label={t.sectionTopModeLabel}
        className="
          flex sm:grid sm:grid-cols-4 gap-2.5 sm:gap-3
          overflow-x-auto sm:overflow-visible
          snap-x snap-mandatory sm:snap-none
          -mx-4 px-4 sm:mx-0 sm:px-0
          pb-1 sm:pb-0
          [scrollbar-width:none] [-ms-overflow-style:none]
          [&amp;::-webkit-scrollbar]:hidden
        "
      >
        {TOP_MODES.map((m) => {
          const Icon = ICONS[m];
          const info = t.topModes[m];
          const active = topMode === m;
          return (
            <button
              key={m}
              role="tab"
              aria-selected={active}
              aria-pressed={active}
              data-testid={`top-mode-${m}`}
              onClick={() => onChange(m)}
              className={`
                snap-start shrink-0 sm:shrink
                min-w-[72%] sm:min-w-0
                text-left border-2 border-black
                p-3.5 sm:p-4
                transition-all relative
                ${
                  active
                    ? "nb-shadow -translate-x-0.5 -translate-y-0.5"
                    : "bg-white nb-shadow-sm hover:-translate-x-0.5 hover:-translate-y-0.5 hover:nb-shadow"
                }
              `}
              style={active ? { background: TOP_MODE_COLORS[m] } : undefined}
            >
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-6 h-6 bg-white border-2 border-black flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
                <h3 className="font-display font-bold text-base sm:text-lg leading-tight tracking-tight uppercase">
                  {info.label}
                </h3>
              </div>
              <p className="text-[12px] sm:text-[13px] text-[#4A4A4A] leading-snug pl-[34px]">
                {info.sub}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
